import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useBodyScrollLock } from '~/hooks/useBodyScrollLock';
import { getItemName } from '~/utils/menuItemHelper';
import type { MenuItem } from '~/types/new_types';

interface ImageZoomModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** The menu item to display */
  item: MenuItem | null;
  /** Default image URL if item has no image */
  defaultImage?: string;
  /** Theme for styling */
  theme: {
    primaryColor?: string;
  } | null;
}

/**
 * Portal-based image zoom modal with body scroll lock.
 * Displays a full-screen view of a menu item image.
 */
export function ImageZoomModal({
  isOpen,
  onClose,
  item,
  defaultImage = '/images/no-image-available.jpg',
  theme,
}: ImageZoomModalProps) {
  const { i18n } = useTranslation();

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  if (!isOpen || !item) return null;

  const imageUrl = item.image_url ?? defaultImage;
  const altText = getItemName(item, i18n.language);

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        {/* Close Button */}
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white transition-colors z-10"
          style={{
            borderRadius: '6px',
            color: theme?.primaryColor,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaTimes size={20} />
        </motion.button>

        {/* Image Container */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
          className="relative w-full flex items-center justify-center"
          style={{ maxHeight: 'calc(100vh - 2rem)' }}
          onClick={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <img
            src={imageUrl}
            alt={altText}
            className="max-w-full max-h-full object-contain rounded-lg"
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/images/broken-image.jpg';
            }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

export default ImageZoomModal;
